export interface AudioFlowMonitorOptions {
  isActive: () => boolean;
  isPaused: () => boolean;
  isPageVisible: () => boolean;
  getContextState: () => AudioContextState | null;
  resumeContext: () => Promise<void>;
  onFailure: (reason: "context-closed" | "frames-stalled") => void;
  stallThresholdMs?: number;
  recoveryGraceMs?: number;
  pollIntervalMs?: number;
  now?: () => number;
}

const DEFAULT_STALL_THRESHOLD_MS = 6_000;
const DEFAULT_RECOVERY_GRACE_MS = 6_000;
const DEFAULT_POLL_INTERVAL_MS = 2_000;

/**
 * Detects a stopped Web Audio render pipeline without treating quiet audio as
 * a failure. Even complete silence produces audio-process callbacks; a lack of
 * callbacks means the pipeline itself is no longer advancing.
 */
export class AudioFlowMonitor {
  private readonly options: Required<
    Pick<
      AudioFlowMonitorOptions,
      "stallThresholdMs" | "recoveryGraceMs" | "pollIntervalMs" | "now"
    >
  > &
    Omit<
      AudioFlowMonitorOptions,
      "stallThresholdMs" | "recoveryGraceMs" | "pollIntervalMs" | "now"
    >;

  private interval: ReturnType<typeof setInterval> | null = null;
  private lastFrameAt = 0;
  private recoveryStartedAt: number | null = null;
  private failureReported = false;

  constructor(options: AudioFlowMonitorOptions) {
    this.options = {
      ...options,
      stallThresholdMs: options.stallThresholdMs ?? DEFAULT_STALL_THRESHOLD_MS,
      recoveryGraceMs: options.recoveryGraceMs ?? DEFAULT_RECOVERY_GRACE_MS,
      pollIntervalMs: options.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS,
      now: options.now ?? Date.now,
    };
  }

  start(): void {
    this.stop();
    this.lastFrameAt = this.options.now();
    this.recoveryStartedAt = null;
    this.failureReported = false;
    this.interval = setInterval(
      () => this.check(),
      this.options.pollIntervalMs
    );
  }

  noteFrame(): void {
    this.lastFrameAt = this.options.now();
    this.recoveryStartedAt = null;
  }

  handleContextStateChange(): void {
    if (!this.options.isActive() || this.options.isPaused()) return;

    const state = this.options.getContextState();
    if (state === "closed") {
      this.reportFailure("context-closed");
    } else if (state === "suspended") {
      void this.options.resumeContext().catch(() => undefined);
    }
  }

  stop(): void {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
    this.recoveryStartedAt = null;
  }

  private check(): void {
    if (
      this.failureReported ||
      !this.options.isActive() ||
      this.options.isPaused() ||
      !this.options.isPageVisible()
    ) {
      return;
    }

    const now = this.options.now();
    if (now - this.lastFrameAt < this.options.stallThresholdMs) {
      this.recoveryStartedAt = null;
      return;
    }

    const state = this.options.getContextState();
    if (state === "closed") {
      this.reportFailure("context-closed");
      return;
    }

    if (state === "suspended") {
      void this.options.resumeContext().catch(() => undefined);
    }

    if (this.recoveryStartedAt === null) {
      this.recoveryStartedAt = now;
      return;
    }

    if (now - this.recoveryStartedAt >= this.options.recoveryGraceMs) {
      this.reportFailure("frames-stalled");
    }
  }

  private reportFailure(reason: "context-closed" | "frames-stalled"): void {
    if (this.failureReported) return;
    this.failureReported = true;
    this.stop();
    this.options.onFailure(reason);
  }
}
